import React, { useEffect, useState } from "react";
import {
  Badge,
  Box,
  Card,
  Flex,
  Heading,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Td,
  Tr,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Image,
  useDisclosure,
} from "@chakra-ui/react";
import { FiChevronDown, FiEye, FiFileText } from "react-icons/fi";
import TableView from "../../components/TableView";
import { baseUrl } from "../../utils/utils";
import { toast } from "react-toastify";
import GasCylinderLoader from "../../components/GasCylinderLoader";

const STATUS_MAP = {
  "0": { label: "Pending", colorScheme: "yellow" },
  "1": { label: "Rejected", colorScheme: "red" },
  "2": { label: "Approved", colorScheme: "green" },
};

const BlkLpgCustomer = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState(10);
  const [initialValue, setInitialValue] = useState(1);
  const [updatingId, setUpdatingId] = useState(null);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewType, setPreviewType] = useState("");

  const fetchList = async (currentPage, currentRows) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("action", "customer_list");
      formData.append("page", currentPage);
      formData.append("limit", currentRows);

      const response = await fetch(baseUrl + "bulklpg.php", {
        method: "POST",
        body: formData,
      });

      const result = await response.json();
      if (result?.success) {
        setData(result.data || []);
        setTotalCount(result.totalcount || 0);
      } else {
        toast.error(result?.message || "Failed to fetch customers");
      }
    } catch (error) {
      console.error("Error fetching blk lpg customers:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    setUpdatingId(id);
    try {
      const formData = new FormData();
      formData.append("action", "update_status");
      formData.append("id", id);
      formData.append("status", status);

      const response = await fetch(baseUrl + "bulklpg.php", {
        method: "POST",
        body: formData,
      });

      const result = await response.json();
      if (result?.success) {
        toast.success(
          `Status updated to ${STATUS_MAP[String(status)]?.label || "Unknown"}`
        );
        fetchList(page, rows);
      } else {
        toast.error(result?.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    } finally {
      setUpdatingId(null);
    }
  };

  useEffect(() => {
    fetchList(page, rows);
  }, [page, rows]);

  const getFileType = (url) => {
    const ext = url?.split(".").pop()?.toLowerCase();
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return "image";
    if (ext === "pdf") return "pdf";
    return "other";
  };

  const handleDocPreview = (url) => {
    const type = getFileType(url);
    if (type === "other") {
      window.open(url, "_blank");
      return;
    }
    setPreviewUrl(url);
    setPreviewType(type);
    onOpen();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <>
      <Card
        bg="#fff"
        paddingInline={5}
        paddingBlock={2}
        flexDirection={["column", "row"]}
        alignItems={["stretch", "center"]}
        gap={3}
        flexWrap="wrap"
      >
        <Heading fontSize="xl">Bulk LPG Customers</Heading>
      </Card>

      <div>
        {loading ? (
          <GasCylinderLoader size="sm" loadingText="Loading Customers" />
        ) : (
          <TableView
            headData={[
              "S.No.",
              "Customer Name",
              "Documents",
              "Status",
              "Date",
              "Action",
            ]}
            body={data.map((el, i) => {
              const status = STATUS_MAP[el.status] || STATUS_MAP["0"];
              const docs = [
                { label: "Doc 1", url: el.doc1 },
                { label: "Doc 2", url: el.doc2 },
                { label: "Doc 3", url: el.doc3 },
              ].filter((d) => d.url);

              return (
                <Tr key={el.id}>
                  <Td textAlign="center">{i + initialValue}</Td>
                  <Td textAlign="center">{el.customer_name}</Td>
                  <Td textAlign="center">
                    <Flex justify="center" gap={2} flexWrap="wrap">
                      {docs.map((doc, idx) => (
                        <Button
                          key={idx}
                          size="xs"
                          colorScheme="blue"
                          variant="outline"
                          leftIcon={
                            getFileType(doc.url) === "pdf" ? (
                              <FiFileText />
                            ) : (
                              <FiEye />
                            )
                          }
                          onClick={() => handleDocPreview(doc.url)}
                        >
                          {doc.label}
                        </Button>
                      ))}
                      {docs.length === 0 && "-"}
                    </Flex>
                  </Td>
                  <Td textAlign="center">
                    <Badge
                      colorScheme={status.colorScheme}
                      px={3}
                      py={1}
                      borderRadius="full"
                      fontSize="xs"
                    >
                      {status.label}
                    </Badge>
                  </Td>
                  <Td textAlign="center" fontSize="sm">
                    {formatDate(el.datetime)}
                  </Td>
                  <Td textAlign="center">
                    <Menu>
                      <MenuButton
                        as={Button}
                        size="sm"
                        rightIcon={<FiChevronDown />}
                        colorScheme="gray"
                        isLoading={updatingId === el.id}
                      >
                        Update Status
                      </MenuButton>
                      <MenuList minW="150px">
                        <MenuItem
                          color="green.600"
                          fontWeight="medium"
                          onClick={() => updateStatus(el.id, 2)}
                        >
                          Approve
                        </MenuItem>
                        <MenuItem
                          color="red.600"
                          fontWeight="medium"
                          onClick={() => updateStatus(el.id, 1)}
                        >
                          Reject
                        </MenuItem>
                        {/* <MenuItem
                          color="yellow.600"
                          fontWeight="medium"
                          onClick={() => updateStatus(el.id, 0)}
                        >
                          Pending
                        </MenuItem> */}
                      </MenuList>
                    </Menu>
                  </Td>
                </Tr>
              );
            })}
            initialValue={initialValue}
            page={page}
            rows={rows}
            total_value={totalCount}
            pagination
            onChange={(e) => {
              const newRows = parseInt(e.target.value);
              setInitialValue(1);
              setPage(1);
              setRows(newRows);
            }}
            onPrevious={() => {
              if (page > 1) {
                const newPage = page - 1;
                setInitialValue((newPage - 1) * rows + 1);
                setPage(newPage);
              }
            }}
            onNext={() => {
              if (rows * page < totalCount) {
                const newPage = page + 1;
                setInitialValue((newPage - 1) * rows + 1);
                setPage(newPage);
              }
            }}
          />
        )}
      </div>

      {/* Document Preview Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent maxW="700px">
          <ModalHeader>Document Preview</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {previewType === "image" ? (
              <Image
                src={previewUrl}
                alt="Document"
                w="100%"
                maxH="500px"
                objectFit="contain"
                borderRadius="md"
              />
            ) : previewType === "pdf" ? (
              <Box
                as="iframe"
                src={previewUrl}
                w="100%"
                h="500px"
                borderRadius="md"
              />
            ) : null}
            <Flex justify="center" mt={4}>
              <Link href={previewUrl} isExternal>
                <Button size="sm" colorScheme="blue">
                  Open in New Tab
                </Button>
              </Link>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default BlkLpgCustomer;
